import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { IStoreAssetApiResponse } from 'models/store/i-store-asset-api-response.interface';
import { IStorePackage } from 'models/store/i-store-package.interface';
import { IWorldNftTradeResponse } from 'models/store/i-world-nft-trade-response.interface';
import { ITradePreviewDetails } from 'models/trade/i-trade-preview-details.interface';

import CompleteCheckoutOfferModal from 'components/modal/completeCheckoutModal/CompleteCheckoutOfferModal';
import CompletePackageCheckoutOfferModal from 'components/modal/packageCheckoutModal/CompletePackageCheckoutOfferModal';

import { IEvents } from 'hooks/events/useEventsParams';

import { getVariationByShares } from 'utils/trade-variations.utils';
import { isWorldNftTradeSuccess } from 'utils/world-nft';
import { convertWorldNftTradeToTradePreviewDetails } from 'utils/world-nft-trade.utils';

interface PackageCheckoutContextType {
  selectedPackage: IStorePackage | null;
  selectedWorldNft: IWorldNftTradeResponse | null;
  selectedWorldNftAssets: IStoreAssetApiResponse | null;
  openCheckout: (packageData: IStorePackage, events?: IEvents) => void;
  openWorldNftCheckout: (
    worldNftData: IWorldNftTradeResponse,
    assetData?: IStoreAssetApiResponse,
    events?: IEvents
  ) => void;
  closeCheckout: () => void;
  isCheckoutOpen: boolean;
  selectedShares: number;
  setSelectedShares: (value: number) => void;
}

const PackageCheckoutContext = createContext<PackageCheckoutContextType | undefined>(undefined);

interface PackageCheckoutProviderProps {
  children: ReactNode;
}

export const PackageCheckoutProvider: React.FC<PackageCheckoutProviderProps> = ({ children }) => {
  const location = useLocation();
  const [selectedPackage, setSelectedPackage] = useState<IStorePackage | null>(null);
  const [selectedWorldNft, setSelectedWorldNft] = useState<IWorldNftTradeResponse | null>(null);
  const [selectedWorldNftAssets, setSelectedWorldNftAssets] = useState<IStoreAssetApiResponse | null>(null);
  const [events, setEvents] = useState<IEvents | undefined>(undefined);
  const [selectedShares, setSelectedShares] = useState<number>(1);
  const [worldNftTradePreviewDetails, setWorldNftTradePreviewDetails] = useState<ITradePreviewDetails | null>(null);

  // Reset selectedShares when route changes
  useEffect(() => {
    setSelectedShares(1);
  }, [location.pathname]);

  // Load world NFT trade preview details when selection changes
  useEffect(() => {
    let cancelled = false;

    const loadWorldNftTradePreview = async () => {
      // Reset state if conditions aren't met
      if (!selectedWorldNft || !selectedWorldNftAssets || !isWorldNftTradeSuccess(selectedWorldNft)) {
        if (!cancelled) {
          setWorldNftTradePreviewDetails(null);
        }
        return;
      }

      try {
        const details = await convertWorldNftTradeToTradePreviewDetails(
          selectedWorldNft,
          selectedWorldNftAssets,
          selectedShares
        );

        // Only update state if component is still mounted and effect hasn't been cancelled
        if (!cancelled) {
          setWorldNftTradePreviewDetails(details);
        }
      } catch (error) {
        console.error('Error loading world NFT trade preview:', error);

        // Only update state if component is still mounted
        if (!cancelled) {
          setWorldNftTradePreviewDetails(null);
        }
      }
    };

    loadWorldNftTradePreview();

    // Cleanup function to prevent state updates on unmounted component
    return () => {
      cancelled = true;
    };
  }, [selectedWorldNft, selectedWorldNftAssets, selectedShares]);

  const openCheckout = useCallback((packageData: IStorePackage, checkoutEvents?: IEvents) => {
    setSelectedPackage(packageData);
    setSelectedWorldNft(null);
    setSelectedWorldNftAssets(null);
    setEvents(checkoutEvents);
  }, []);

  const openWorldNftCheckout = useCallback(
    (worldNftData: IWorldNftTradeResponse, assetData?: IStoreAssetApiResponse, checkoutEvents?: IEvents) => {
      setSelectedWorldNft(worldNftData);
      setSelectedWorldNftAssets(assetData || null);
      setSelectedPackage(null);
      setEvents(checkoutEvents);
    },
    []
  );

  const closeCheckout = useCallback(() => {
    setSelectedPackage(null);
    setSelectedWorldNft(null);
    setSelectedWorldNftAssets(null);
    setWorldNftTradePreviewDetails(null);
    setEvents(undefined);
  }, []);

  const value = useMemo<PackageCheckoutContextType>(
    () => ({
      selectedPackage,
      selectedWorldNft,
      selectedWorldNftAssets,
      openCheckout,
      openWorldNftCheckout,
      closeCheckout,
      isCheckoutOpen: selectedPackage !== null || selectedWorldNft !== null,
      selectedShares,
      setSelectedShares,
    }),
    [
      selectedPackage,
      selectedWorldNft,
      selectedWorldNftAssets,
      openCheckout,
      openWorldNftCheckout,
      closeCheckout,
      selectedShares,
      setSelectedShares,
    ]
  );

  const selectedOffer = useMemo(
    () =>
      selectedPackage
        ? getVariationByShares(selectedPackage.available_trade_offers_by_variation, selectedShares)
        : null,
    [selectedPackage, selectedShares]
  );

  return (
    <PackageCheckoutContext.Provider value={value}>
      {children}
      {selectedPackage && selectedOffer && (
        <CompletePackageCheckoutOfferModal
          packageDetails={selectedPackage}
          onClose={closeCheckout}
          onFinished={closeCheckout}
          events={events}
          selectedOffer={selectedOffer}
        />
      )}
      {worldNftTradePreviewDetails && (
        <CompleteCheckoutOfferModal
          tradePreviewDetails={worldNftTradePreviewDetails}
          onClose={closeCheckout}
          onFinished={closeCheckout}
          events={events}
        />
      )}
    </PackageCheckoutContext.Provider>
  );
};

export const usePackageCheckout = (): PackageCheckoutContextType => {
  const context = useContext(PackageCheckoutContext);
  if (context === undefined) {
    throw new Error('usePackageCheckout must be used within a PackageCheckoutProvider');
  }
  return context;
};
